import {
  NormalizedOrder,
  OrdersResponse,
  OrdersSummary,
} from '../types/unwinding.types';

const DEFAULT_DATE = new Date(0).toISOString();

const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const asBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  if (typeof value === 'number') return value === 1;
  return false;
};

const asIsoDate = (...candidates: unknown[]): string => {
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      const millis = candidate > 1e12 ? candidate : candidate * 1000;
      const date = new Date(millis);
      if (!Number.isNaN(date.getTime())) return date.toISOString();
      continue;
    }

    if (typeof candidate === 'string' && candidate.trim() !== '') {
      const numericCandidate = Number(candidate);
      if (Number.isFinite(numericCandidate)) {
        const millis = numericCandidate > 1e12
          ? numericCandidate
          : numericCandidate * 1000;
        const numericDate = new Date(millis);
        if (!Number.isNaN(numericDate.getTime())) return numericDate.toISOString();
      }

      const parsed = new Date(candidate);
      if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
    }
  }

  return DEFAULT_DATE;
};

const normalizePlatform = (
  value: unknown,
  fallbackOrder?: Record<string, unknown>,
): 'kalshi' | 'polymarket' => {
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (normalized.includes('kalshi')) return 'kalshi';
    if (normalized.includes('poly')) return 'polymarket';
  }

  if (fallbackOrder) {
    if ('ticker' in fallbackOrder || 'yes_price' in fallbackOrder) return 'kalshi';
    if ('asset_id' in fallbackOrder || 'tokenId' in fallbackOrder) return 'polymarket';
  }

  return 'polymarket';
};

const normalizeSide = (value: unknown): 'yes' | 'no' => {
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (normalized === 'yes' || normalized === 'y') return 'yes';
    if (normalized === 'no' || normalized === 'n') return 'no';
  }
  return 'yes';
};

const normalizeAction = (value: unknown): 'buy' | 'sell' => {
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (normalized === 'sell' || normalized === 'ask') return 'sell';
  }
  return 'buy';
};

const normalizeStatus = (value: unknown): string => {
  if (typeof value !== 'string' || value.trim() === '') return 'unknown';
  return value.toLowerCase().replace('order_status_', '');
};

const normalizePrice = (
  order: Record<string, unknown>,
  side: 'yes' | 'no',
): number => {
  const directPrice = asNumber(
    order.price ?? order.limitPrice ?? order.limit_price ?? order.rate,
  );
  if (directPrice != null) return directPrice;

  const dollarsField = side === 'yes'
    ? order.yes_price_dollars
    : order.no_price_dollars;
  const dollarsPrice = asNumber(dollarsField);
  if (dollarsPrice != null) return dollarsPrice;

  const centsField = side === 'yes' ? order.yes_price : order.no_price;
  const centsPrice = asNumber(centsField);
  if (centsPrice == null) return 0;
  return centsPrice > 1 ? centsPrice / 100 : centsPrice;
};

const toOrderRecord = (
  candidate: unknown,
  platformHint?: 'kalshi' | 'polymarket',
): NormalizedOrder | null => {
  if (!isObject(candidate)) return null;

  const platform = normalizePlatform(candidate.platform ?? candidate.exchange ?? platformHint, candidate);
  const side = normalizeSide(candidate.side ?? candidate.outcome ?? candidate.positionSide);
  const action = normalizeAction(candidate.action ?? candidate.orderSide ?? candidate.direction);
  const quantity = Math.max(
    0,
    asNumber(candidate.quantity ?? candidate.count ?? candidate.original_size ?? candidate.size) ?? 0,
  );
  const filledQuantity = Math.max(
    0,
    asNumber(
      candidate.filledQuantity ??
        candidate.filled_quantity ??
        candidate.size_matched ??
        candidate.filledSize ??
        candidate.executed_count,
    ) ?? 0,
  );
  const remainingQuantity = Math.max(
    0,
    asNumber(
      candidate.remainingQuantity ??
        candidate.remaining_quantity ??
        candidate.remaining_count ??
        candidate.open_size,
    ) ?? (quantity - filledQuantity),
  );
  const price = normalizePrice(candidate, side);
  const id = String(
    candidate.id ??
      candidate.orderId ??
      candidate.order_id ??
      candidate.clientOrderId ??
      `${platform}:${candidate.marketId ?? candidate.market_id ?? candidate.ticker ?? candidate.asset_id ?? 'unknown'}:${candidate.createdAt ?? candidate.created_at ?? Math.random()}`,
  );

  const marketId = String(
    candidate.marketId ??
      candidate.market_id ??
      candidate.ticker ??
      candidate.asset_id ??
      candidate.tokenId ??
      candidate.market ??
      candidate.conditionId ??
      'unknown',
  );

  const createdAt = asIsoDate(
    candidate.createdAt,
    candidate.created_at,
    candidate.timestamp,
    candidate.time,
  );
  const updatedAt = asIsoDate(
    candidate.updatedAt,
    candidate.updated_at,
    candidate.timestamp,
    candidate.createdAt,
    candidate.created_at,
  );

  return {
    id,
    platform,
    marketId,
    marketTitle: typeof candidate.marketTitle === 'string'
      ? candidate.marketTitle
      : typeof candidate.market_title === 'string'
        ? candidate.market_title
        : typeof candidate.title === 'string'
          ? candidate.title
          : undefined,
    marketUrl: typeof candidate.marketUrl === 'string'
      ? candidate.marketUrl
      : typeof candidate.market_url === 'string'
        ? candidate.market_url
        : typeof candidate.url === 'string'
          ? candidate.url
          : undefined,
    side,
    action,
    price,
    quantity,
    filledQuantity,
    remainingQuantity,
    status: normalizeStatus(candidate.status ?? candidate.state),
    isUnwinding: asBoolean(candidate.isUnwinding ?? candidate.unwinding ?? candidate.is_unwinding),
    ruleId: typeof candidate.ruleId === 'string'
      ? candidate.ruleId
      : typeof candidate.rule_id === 'string'
        ? candidate.rule_id
        : undefined,
    createdAt,
    updatedAt,
  };
};

const collectOrders = (payload: unknown): NormalizedOrder[] => {
  const pushAll = (
    source: unknown,
    destination: NormalizedOrder[],
    platformHint?: 'kalshi' | 'polymarket',
  ) => {
    if (Array.isArray(source)) {
      for (const item of source) {
        const normalized = toOrderRecord(item, platformHint);
        if (normalized) destination.push(normalized);
      }
      return;
    }

    if (!isObject(source)) return;

    if (Array.isArray(source.orders)) {
      pushAll(source.orders, destination, platformHint);
      return;
    }

    if (Array.isArray(source.data)) {
      pushAll(source.data, destination, platformHint);
    }
  };

  const collected: NormalizedOrder[] = [];

  if (Array.isArray(payload)) {
    pushAll(payload, collected);
  } else if (isObject(payload)) {
    pushAll(payload.orders ?? payload.data ?? payload, collected);

    // Legacy/per-platform envelope support:
    // { kalshi: [...] , polymarket: [...] } or nested objects with orders/data.
    if (payload.kalshi) pushAll(payload.kalshi, collected, 'kalshi');
    if (payload.polymarket) pushAll(payload.polymarket, collected, 'polymarket');

    // Optional split buckets from older responses.
    if (payload.activeOrders) pushAll(payload.activeOrders, collected);
    if (payload.closedOrders) pushAll(payload.closedOrders, collected);
  }

  const deduped = new Map<string, NormalizedOrder>();
  for (const order of collected) {
    deduped.set(`${order.platform}:${order.id}`, order);
  }

  return Array.from(deduped.values()).sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
};

const normalizeSummary = (
  payload: unknown,
  orders: NormalizedOrder[],
): OrdersSummary => {
  const computed: OrdersSummary = {
    kalshi: {
      count: orders.filter((o) => o.platform === 'kalshi').length,
      totalVolume: orders
        .filter((o) => o.platform === 'kalshi')
        .reduce((sum, o) => sum + o.price * o.quantity, 0),
    },
    polymarket: {
      count: orders.filter((o) => o.platform === 'polymarket').length,
      totalVolume: orders
        .filter((o) => o.platform === 'polymarket')
        .reduce((sum, o) => sum + o.price * o.quantity, 0),
    },
  };

  if (!isObject(payload) || !isObject(payload.summary)) return computed;

  const summary = payload.summary;
  const platformSummary = (
    candidate: unknown,
    fallback: { count: number; totalVolume: number },
  ): { count: number; totalVolume: number } => {
    if (!isObject(candidate)) return fallback;
    return {
      count: asNumber(candidate.count ?? candidate.total ?? candidate.ordersCount) ?? fallback.count,
      totalVolume:
        asNumber(candidate.totalVolume ?? candidate.total_volume ?? candidate.volumeUsd) ??
        fallback.totalVolume,
    };
  };

  return {
    kalshi: platformSummary(summary.kalshi, computed.kalshi),
    polymarket: platformSummary(summary.polymarket, computed.polymarket),
  };
};

export const normalizeOrdersPayload = (payload: unknown): OrdersResponse => {
  const orders = collectOrders(payload);
  const summary = normalizeSummary(payload, orders);
  return { orders, summary };
};
